/**
 * useTaskGrouping Hook
 *
 * Feature: 650-task-grouping
 *
 * Provides task grouping state and operations.
 * Runs multiple grouping strategies and allows toggling which strategies are visible.
 */

import { useMemo, useState, useCallback } from 'react';
import type { Task } from '@/hooks/graphql/useTaskMutationsGraphQL';
import type {
  GroupingStrategy,
  UseTaskGroupingResult,
  MultiGroupingResult,
  TaskGroup,
} from '@/types/task-groups';
import { groupTasks } from '@/utils/task-grouping/group-tasks';
import { allStrategies } from '@/utils/task-grouping/grouping-strategies';

/**
 * Hook for managing task grouping with multiple strategies.
 *
 * @param tasks - Array of tasks to group
 * @param strategies - Grouping strategies to apply (default: all strategies)
 * @returns Grouping state and operations
 */
export function useTaskGrouping(
  tasks: Task[],
  strategies: GroupingStrategy[] = allStrategies
): UseTaskGroupingResult {
  // State for expand/collapse
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // State for grouping enabled/disabled
  const [groupingEnabled, setGroupingEnabled] = useState(true);

  // State for which strategies are enabled (default: all)
  const [enabledStrategies, setEnabledStrategies] = useState<Set<string>>(
    () => new Set(strategies.map(s => s.id))
  );

  // Run ALL strategies - same task can appear in multiple groups
  const allResults = useMemo<MultiGroupingResult>(() => {
    const byStrategy = new Map(
      strategies.map(strategy => [strategy.id, groupTasks(tasks, strategy)])
    );

    return {
      byStrategy,
      activeStrategyId: '', // Not used - all enabled strategies shown
      availableStrategies: strategies.map(s => s.id),
      getActiveResult() {
        return undefined;
      },
    };
  }, [tasks, strategies]);

  // Flatten and filter groups from all ENABLED strategies
  const visibleGroups = useMemo<TaskGroup[]>(() => {
    if (!groupingEnabled) return [];

    const allGroups: TaskGroup[] = [];
    for (const [strategyId, result] of allResults.byStrategy) {
      if (enabledStrategies.has(strategyId)) {
        allGroups.push(...result.groups);
      }
    }
    return allGroups;
  }, [allResults, enabledStrategies, groupingEnabled]);

  // Tasks not in any visible group
  const ungroupedTasks = useMemo<Task[]>(() => {
    if (!groupingEnabled) return tasks;

    const groupedTaskIds = new Set(visibleGroups.flatMap(g => g.tasks.map(t => t.id)));
    return tasks.filter(t => !groupedTaskIds.has(t.id));
  }, [tasks, visibleGroups, groupingEnabled]);

  // Toggle a specific group's expansion
  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  // Expand all groups
  const expandAll = useCallback(() => {
    setExpandedGroups(new Set(visibleGroups.map(g => g.key)));
  }, [visibleGroups]);

  // Collapse all groups
  const collapseAll = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  // Check if a group is expanded
  const isExpanded = useCallback(
    (groupKey: string) => expandedGroups.has(groupKey),
    [expandedGroups]
  );

  // Toggle a strategy's visibility
  const toggleStrategy = useCallback((strategyId: string) => {
    setEnabledStrategies(prev => {
      const next = new Set(prev);
      if (next.has(strategyId)) {
        next.delete(strategyId);
      } else {
        next.add(strategyId);
      }
      return next;
    });
  }, []);

  // Check if a strategy is enabled
  const isStrategyEnabled = useCallback(
    (strategyId: string) => enabledStrategies.has(strategyId),
    [enabledStrategies]
  );

  // Enable all strategies
  const enableAllStrategies = useCallback(() => {
    setEnabledStrategies(new Set(strategies.map(s => s.id)));
  }, [strategies]);

  // Disable all strategies
  const disableAllStrategies = useCallback(() => {
    setEnabledStrategies(new Set());
  }, []);

  // Toggle grouping on/off entirely
  const toggleGrouping = useCallback(() => {
    setGroupingEnabled(prev => !prev);
  }, []);

  return {
    visibleGroups,
    ungroupedTasks,
    allResults,
    state: {
      expandedGroups,
      enabledStrategies,
      groupingEnabled,
    },
    toggleGroup,
    expandAll,
    collapseAll,
    isExpanded,
    toggleStrategy,
    isStrategyEnabled,
    enableAllStrategies,
    disableAllStrategies,
    toggleGrouping,
    availableStrategies: strategies.map(s => s.id),
  };
}
